// routes/index.tsx
import { lazy } from 'react';
import { Navigate, RouteObject } from 'react-router-dom';

import AppLayout from '@/v2/layouts/AppLayout';
import { PATH } from '@/v2/utils/constants';
import { GuestGuard } from '@/commons/components/GuestGuard';
import { AuthGuard } from '@/commons/components/AuthGuard';
import { PermissionGuard } from '@/commons/components/PermissionGuard';
import { Permission } from '@/commons/services/permissions.service';

const LoginPage = lazy(() => import('@/commons/LoginPage'));
const DashboardPage = lazy(() => import('@/v2/pages/DashboardPage'));
const ChatUIPage = lazy(() => import('@/v2/pages/ChatUIPage'));
const ChatSettingsPage = lazy(
  () => import('@/v2/pages/chat-settings/ChatSettingsPage'),
);
const DesignSettingPage = lazy(() => import('@/v2/pages/DesignSettingPage'));
const AvatarSettingPage = lazy(() => import('@/v2/pages/AvatarSettingPage'));
const AnalyticsPage = lazy(() => import('@/v2/pages/AnalyticsPage'));
const UrlSettingPage = lazy(() => import('@/v2/pages/UrlSettingPage'));
const FormHistoryPage = lazy(() => import('@/v2/pages/FormHistoryPage'));
const FormsPage = lazy(
  () => import('@/v2/pages/chat-settings/forms/FormsPage'),
);

const FixedMessage = lazy(
  () => import('@/v2/pages/chat-settings/FixedMessagePage'),
);
const RegisterFormsPage = lazy(
  () => import('@/v2/pages/chat-settings/forms/RegisterFormsPage'),
);
const ConversationHistoryPage = lazy(
  () => import('@/v2/pages/ConversationHistoryPage'),
);
const DetailConversationHistoryPage = lazy(
  () => import('@/v2/pages/DetailConversationHistoryPage'),
);

const QuickReply = lazy(
  () => import('@/v2/pages/chat-settings/QuickReplyPage'),
);
const VoiceSettingPage = lazy(() => import('@/v2/pages/VoiceSettingPage'));
const EmotionSettingPage = lazy(() => import('@/v2/pages/EmotionSettingPage'));
const BasicAgentSettingPage = lazy(
  () => import('@/v2/pages/BasicAgentSettingPage'),
);
const MediaLibraryPage = lazy(
  () => import('@/v2/pages/chat-settings/MediaLibraryPage'),
);
const DetailAiSettingsPage = lazy(
  () => import('@/v2/pages/chat-settings/DetailAISettingPage'),
);
const KnowledgeDataStoreListPage = lazy(
  () =>
    import(
      '@/v2/pages/chat-settings/knowledge-data-stores/KnowledgeDataStoreListPage'
    ),
);
const KnowledgeDataStoreRegisterPage = lazy(
  () =>
    import(
      '@/v2/pages/chat-settings/knowledge-data-stores/KnowledgeDataStoreRegisterPage'
    ),
);
const KnowledgeDataStoreEditPage = lazy(
  () =>
    import(
      '@/v2/pages/chat-settings/knowledge-data-stores/KnowledgeDataStoreEditPage'
    ),
);
const LineLoginFormPage = lazy(
  () => import('@/v2/pages/chat-settings/line-login-datas/LineLoginFormPage'),
);
const AccountListPage = lazy(
  () => import('@/v2/pages/chat-settings/line-login-datas/AccountListPage'),
);

const ForbiddenUrlPage = lazy(
  () => import('@/v2/pages/chat-settings/ForbiddenUrlPage'),
);

const ForbiddenWordPage = lazy(
  () => import('@/v2/pages/chat-settings/ForbiddenWordPage'),
);

const VoiceModePage = lazy(() => import('@/v2/pages/chat-settings/VoiceMode'));
const FAQSettingPage = lazy(() => import('@/v2/pages/FAQSettingPage'));
const FAQSettingWithAIPage = lazy(() => import('@/v2/pages/FAQSettingWithAI'));
const FAQWithAIRegisterPage = lazy(
  () => import('@/v2/pages/FAQWithAIRegisterPage'),
);
const FAQRegisterPage = lazy(() => import('@/v2/pages/FAQRegisterPage'));
const EmotionListPage = lazy(() => import('@/v2/pages/EmotionListPage'));
const AccountSettingPage = lazy(() => import('@/v2/pages/AccountSettingPage'));
const PlanAndBillingPage = lazy(() => import('@/v2/pages/PlanAndBillingPage'));
const PublicSettingsPage = lazy(() => import('@/v2/pages/PublicSettingsPage'));
export const routes: RouteObject[] = [
  {
    path: PATH.LOGIN,
    element: (
      <GuestGuard>
        <LoginPage />
      </GuestGuard>
    ),
  },
  {
    path: PATH.DASHBOARD,
    element: <AppLayout />,
    children: [
      {
        index: true,
        element: (
          <AuthGuard>
            <PermissionGuard requiredPermissions={[Permission.BASIC_USAGE]}>
              <DashboardPage />
            </PermissionGuard>
          </AuthGuard>
        ),
      },
      {
        path: PATH.CHAT_UI,
        element: (
          <AuthGuard>
            <PermissionGuard
              requiredPermissions={[
                Permission.BASIC_USAGE,
                Permission.CONVERSATION_TEST,
              ]}
            >
              <ChatUIPage />
            </PermissionGuard>
          </AuthGuard>
        ),
      },
      {
        path: PATH.CHAT_SETTINGS,
        children: [
          {
            index: true,
            element: (
              <AuthGuard>
                <PermissionGuard requiredPermissions={[Permission.BASIC_USAGE]}>
                  <ChatSettingsPage />
                </PermissionGuard>
              </AuthGuard>
            ),
          },
          {
            path: 'quickreply',
            element: (
              <AuthGuard>
                <PermissionGuard requiredPermissions={[Permission.BASIC_USAGE]}>
                  <QuickReply />
                </PermissionGuard>
              </AuthGuard>
            ),
          },
          {
            path: 'medialibrary',
            element: (
              <AuthGuard>
                <PermissionGuard
                  requiredPermissions={[
                    Permission.BASIC_USAGE,
                    Permission.MEDIA_LIBRARY,
                  ]}
                >
                  <MediaLibraryPage />
                </PermissionGuard>
              </AuthGuard>
            ),
          },
          {
            path: 'fixedmessage',
            element: (
              <AuthGuard>
                <PermissionGuard requiredPermissions={[Permission.BASIC_USAGE]}>
                  <FixedMessage />
                </PermissionGuard>
              </AuthGuard>
            ),
          },
          {
            path: 'detailaisettings',
            element: (
              <AuthGuard>
                <PermissionGuard
                  requiredPermissions={[
                    Permission.BASIC_USAGE,
                    Permission.PROMPT_EDITING,
                  ]}
                >
                  <DetailAiSettingsPage />
                </PermissionGuard>
              </AuthGuard>
            ),
          },
          {
            path: 'knowledgedatastore',
            children: [
              {
                index: true,
                element: (
                  <AuthGuard>
                    <PermissionGuard
                      requiredPermissions={[Permission.BASIC_USAGE]}
                    >
                      <KnowledgeDataStoreListPage />
                    </PermissionGuard>
                  </AuthGuard>
                ),
              },
              {
                path: 'register',
                element: (
                  <AuthGuard>
                    <PermissionGuard
                      requiredPermissions={[Permission.BASIC_USAGE]}
                    >
                      <KnowledgeDataStoreRegisterPage />
                    </PermissionGuard>
                  </AuthGuard>
                ),
              },
              {
                path: 'edit/:id',
                element: (
                  <AuthGuard>
                    <PermissionGuard
                      requiredPermissions={[Permission.BASIC_USAGE]}
                    >
                      <KnowledgeDataStoreEditPage />
                    </PermissionGuard>
                  </AuthGuard>
                ),
              },
            ],
          },
          {
            path: 'makeforms',
            children: [
              {
                index: true,
                element: (
                  <AuthGuard>
                    <PermissionGuard
                      requiredPermissions={[
                        Permission.BASIC_USAGE,
                        Permission.FORM_FUNCTION,
                      ]}
                    >
                      <FormsPage />
                    </PermissionGuard>
                  </AuthGuard>
                ),
              },
              {
                path: 'register',
                element: (
                  <AuthGuard>
                    <PermissionGuard
                      requiredPermissions={[
                        Permission.BASIC_USAGE,
                        Permission.FORM_FUNCTION,
                      ]}
                    >
                      <RegisterFormsPage />
                    </PermissionGuard>
                  </AuthGuard>
                ),
              },
              {
                path: 'edit/:id',
                element: (
                  <AuthGuard>
                    <PermissionGuard
                      requiredPermissions={[
                        Permission.BASIC_USAGE,
                        Permission.FORM_FUNCTION,
                      ]}
                    >
                      <RegisterFormsPage />
                    </PermissionGuard>
                  </AuthGuard>
                ),
              },
            ],
          },
          {
            path: 'forbiddenurl',
            element: (
              <AuthGuard>
                <PermissionGuard requiredPermissions={[Permission.BASIC_USAGE]}>
                  <ForbiddenUrlPage />
                </PermissionGuard>
              </AuthGuard>
            ),
          },
          {
            path: 'forbiddenword',
            element: (
              <AuthGuard>
                <PermissionGuard requiredPermissions={[Permission.BASIC_USAGE]}>
                  <ForbiddenWordPage />
                </PermissionGuard>
              </AuthGuard>
            ),
          },
          {
            path: 'linelogin',
            children: [
              {
                index: true,
                element: (
                  <AuthGuard>
                    <PermissionGuard
                      requiredPermissions={[
                        Permission.BASIC_USAGE,
                        Permission.LINE_LOGIN,
                        Permission.LINE_INTEGRATION,
                      ]}
                    >
                      <LineLoginFormPage />
                    </PermissionGuard>
                  </AuthGuard>
                ),
              },
              {
                path: 'account-list',
                element: (
                  <AuthGuard>
                    <PermissionGuard
                      requiredPermissions={[
                        Permission.BASIC_USAGE,
                        Permission.LINE_LOGIN,
                        Permission.LINE_INTEGRATION,
                      ]}
                    >
                      <AccountListPage />
                    </PermissionGuard>
                  </AuthGuard>
                ),
              },
            ],
          },
          {
            path: 'voicemode',
            element: (
              <AuthGuard>
                <PermissionGuard
                  requiredPermissions={[
                    Permission.BASIC_USAGE,
                    Permission.VOICE_MODE,
                  ]}
                >
                  <VoiceModePage />
                </PermissionGuard>
              </AuthGuard>
            ),
          },
        ],
      },
      {
        path: PATH.AVATAR_SETTINGS,
        children: [
          {
            index: true,
            element: (
              <AuthGuard>
                <PermissionGuard requiredPermissions={[Permission.BASIC_USAGE]}>
                  <AvatarSettingPage />
                </PermissionGuard>
              </AuthGuard>
            ),
          },
          {
            path: 'voicesettings',
            element: (
              <AuthGuard>
                <PermissionGuard
                  requiredPermissions={[
                    Permission.BASIC_USAGE,
                    Permission.CUSTOM_CHARACTER,
                    Permission.VOICE_MODE,
                    Permission.VOICE_SPEECH,
                  ]}
                >
                  <VoiceSettingPage />
                </PermissionGuard>
              </AuthGuard>
            ),
          },

          {
            path: 'emotions-list',
            element: (
              <AuthGuard>
                <PermissionGuard requiredPermissions={[Permission.BASIC_USAGE]}>
                  <EmotionListPage />
                </PermissionGuard>
              </AuthGuard>
            ),
            children: [],
          },
          {
            path: 'emotions-setting',
            element: (
              <AuthGuard>
                <PermissionGuard
                  requiredPermissions={[
                    Permission.BASIC_USAGE,
                    Permission.CUSTOM_CHARACTER,
                  ]}
                >
                  <EmotionSettingPage />
                </PermissionGuard>
              </AuthGuard>
            ),
          },
          {
            path: 'basicagentsettings',
            element: (
              <AuthGuard>
                <PermissionGuard requiredPermissions={[Permission.BASIC_USAGE]}>
                  <BasicAgentSettingPage />
                </PermissionGuard>
              </AuthGuard>
            ),
          },
        ],
      },
      {
        path: PATH.DESIGN_SETTINGS,
        element: (
          <AuthGuard>
            <PermissionGuard requiredPermissions={[Permission.BASIC_USAGE]}>
              <DesignSettingPage />
            </PermissionGuard>
          </AuthGuard>
        ),
      },
      {
        path: PATH.URL_SETTINGS,
        element: (
          <AuthGuard>
            <PermissionGuard
              requiredPermissions={[
                Permission.BASIC_USAGE,
                Permission.LINK_SETTING,
              ]}
            >
              <UrlSettingPage />
            </PermissionGuard>
          </AuthGuard>
        ),
      },
      {
        path: PATH.ANALYTICS,
        element: (
          <AuthGuard>
            <PermissionGuard
              requiredPermissions={[
                Permission.BASIC_USAGE,
                Permission.ANALYTICS,
              ]}
            >
              <AnalyticsPage />
            </PermissionGuard>
          </AuthGuard>
        ),
      },
      {
        path: PATH.CONVERSATION_HISTORY,
        element: (
          <AuthGuard>
            <PermissionGuard requiredPermissions={[Permission.BASIC_USAGE]}>
              <ConversationHistoryPage />
            </PermissionGuard>
          </AuthGuard>
        ),
      },
      {
        path: PATH.CONVERSATION_HISTORY_DETAIL,
        element: (
          <AuthGuard>
            <PermissionGuard requiredPermissions={[Permission.BASIC_USAGE]}>
              <DetailConversationHistoryPage />
            </PermissionGuard>
          </AuthGuard>
        ),
      },
      {
        path: PATH.FORM_HISTORY,
        element: (
          <AuthGuard>
            <PermissionGuard
              requiredPermissions={[
                Permission.BASIC_USAGE,
                Permission.FORM_FUNCTION,
              ]}
            >
              <FormHistoryPage />
            </PermissionGuard>
          </AuthGuard>
        ),
      },
      {
        path: PATH.FAQ_SETTING,
        element: (
          <AuthGuard>
            <PermissionGuard
              requiredPermissions={[
                Permission.BASIC_USAGE,
                Permission.FAQ_SETTING,
              ]}
            >
              <FAQSettingPage />
            </PermissionGuard>
          </AuthGuard>
        ),
      },
      {
        path: PATH.FAQ_SETTING_WITH_AI,
        element: (
          <AuthGuard>
            <PermissionGuard
              requiredPermissions={[
                Permission.BASIC_USAGE,
                Permission.FAQ_SETTING,
              ]}
            >
              <FAQSettingWithAIPage />
            </PermissionGuard>
          </AuthGuard>
        ),
      },
      {
        path: `${PATH.FAQ_EDIT_WITH_AI}/:id`,
        element: (
          <AuthGuard>
            <PermissionGuard
              requiredPermissions={[
                Permission.BASIC_USAGE,
                Permission.FAQ_SETTING,
              ]}
            >
              <FAQWithAIRegisterPage />
            </PermissionGuard>
          </AuthGuard>
        ),
      },
      {
        path: `${PATH.FAQ_REGISTER}`,
        element: (
          <AuthGuard>
            <PermissionGuard
              requiredPermissions={[
                Permission.BASIC_USAGE,
                Permission.FAQ_SETTING,
              ]}
            >
              <FAQRegisterPage />
            </PermissionGuard>
          </AuthGuard>
        ),
      },
      {
        path: `${PATH.FAQ_EDIT}/:id`,
        element: (
          <AuthGuard>
            <PermissionGuard
              requiredPermissions={[
                Permission.BASIC_USAGE,
                Permission.FAQ_SETTING,
              ]}
            >
              <FAQRegisterPage />
            </PermissionGuard>
          </AuthGuard>
        ),
      },
      {
        path: PATH.ACCOUNT_SETTING,
        element: (
          <AuthGuard>
            <PermissionGuard>
              <AccountSettingPage />
            </PermissionGuard>
          </AuthGuard>
        ),
      },
      {
        path: PATH.PLAN_AND_BILLING,
        element: (
          <AuthGuard>
            <PermissionGuard>
              <PlanAndBillingPage />
            </PermissionGuard>
          </AuthGuard>
        ),
      },
      {
        path: PATH.PUBLIC_SETTINGS,
        element: (
          <AuthGuard>
            <PermissionGuard requiredPermissions={[Permission.BASIC_USAGE]}>
              <PublicSettingsPage />
            </PermissionGuard>
          </AuthGuard>
        ),
      },
      {
        path: '*',
        element: <Navigate to={PATH.NOT_FOUND} replace />,
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to={PATH.NOT_FOUND} replace />,
  },
];
